import { useAppDispatch, useAppSelector } from "@store/store";
import { selectPlan } from "@store/plan/planReducer";
import {
  forwardRef,
  FunctionComponent,
  useCallback,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  selectOverlayScreen,
  setOverlayScreenAction,
} from "@store/app/appReducer";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Text } from "@ui-kit/Text/Text";
import { Button } from "@ui-kit/Button/Button";

// Extend dayjs with the isSameOrBefore plugin
dayjs.extend(isSameOrBefore);

import "./PlanAddToDatePicker.css";
import { useAddRemoveToPlan } from "@modules/components/buttons/AddRemoveButton/useAddRemoveToPlan";
import {
  OverlayFooterProps,
  OverlayRef,
} from "@modules/overlayRouter/OverlayProps";
import { useTranslation } from "react-i18next";

export const PlanAddToDatePicker = forwardRef<OverlayRef>((props, ref) => {
  const dispatch = useAppDispatch();
  const plan = useAppSelector(selectPlan);
  const overlayScreen = useAppSelector(selectOverlayScreen);
  const [onAddOrRemovePlanItem, { isLoading: isAddingToPlan }] =
    useAddRemoveToPlan(
      overlayScreen?.params?.entityId as number,
      (overlayScreen?.params?.entityType as "Place" | "Event") ?? "Place",
    );

  const handleCancel = useCallback(() => {
    onAddOrRemovePlanItem(null);
    dispatch(setOverlayScreenAction());
  }, [dispatch, onAddOrRemovePlanItem]);

  useImperativeHandle(
    ref,
    () => ({
      cancel: handleCancel,
    }),
    [handleCancel],
  );

  const dateOptions = useMemo(() => {
    if (plan?.startsOn && plan?.endsOn) {
      // Generate array of dates between startsOn and endsOn inclusive
      const dates: string[] = [];
      let currentDate = dayjs(plan.startsOn);
      const endDate = dayjs(plan.endsOn);

      while (currentDate.isSameOrBefore(endDate)) {
        dates.push(currentDate.format("M/D"));
        currentDate = currentDate.add(1, "day");
      }
      return dates;
    } else if (plan?.dayDuration) {
      // Generate array of day numbers from 1 to dayDuration
      return Array.from({ length: plan.dayDuration }, (_, i) => `Day ${i + 1}`);
    }
    return ["Unscheduled"];
  }, [plan]);

  const handleDaySelect = useCallback(
    (dayIndex: number | null) => () => {
      onAddOrRemovePlanItem(dayIndex);
      dispatch(setOverlayScreenAction());
    },
    [dispatch, onAddOrRemovePlanItem],
  );

  return (
    <div className="plan-add-to-date-picker-component">
      <div className="date-options">
        {dateOptions.map((label, index) => (
          <TabButton
            key={label}
            label={label}
            onPress={handleDaySelect(index + 1)}
            variant="outline"
            disabled={isAddingToPlan}
            medium
          />
        ))}
        {dateOptions.length > 1 && (
          <TabButton
            label="Unscheduled"
            onPress={handleDaySelect(null)}
            variant="outline"
            disabled={isAddingToPlan}
            medium
          />
        )}
      </div>
    </div>
  );
});

PlanAddToDatePicker.displayName = "PlanAddToDatePicker";

export const PlanAddToDatePickerHeader = () => {
  const { t } = useTranslation();
  return (
    <Text variant="fontHeadingMedium">{t("screens.plan.addToPlanDay")}</Text>
  );
};

export const PlanAddToDatePickerFooter: FunctionComponent<
  OverlayFooterProps
> = ({ overlayRef }) => {
  const { t } = useTranslation();
  const handleCancel = useCallback(() => {
    console.log("handleCancel");
    if (overlayRef.current?.cancel) {
      console.log("overlayRef.current.cancel");
      overlayRef.current.cancel();
    }
  }, [overlayRef]);

  return (
    <Button type="text" onPress={handleCancel}>
      {t("common.skip")} asdf
    </Button>
  );
};
